/**
 * Family-cluster graph view (default mode).
 *
 * Aggregates the server-provided candidate list by family, producing one node
 * per family. Node size and opacity scale with summed candidate probability so
 * the leading families read clearly even when the field is broad.
 *
 * Layout uses bounded preset positions rather than one rank per concentric
 * ring. Broad candidate pools can contain many families; an unbounded ring
 * layout forces Cytoscape to fit a huge field until nodes become invisible.
 */
import type { ConverseCandidate } from '../../lib/api/converse.js';
import { colors } from '../../theme/tokens.js';

export interface FamilyElement {
  data: { id: string; label: string; displayLabel: string; count: number; mass: number; sizeScore: number; topP: number; rank: number };
  position: { x: number; y: number };
}

const MAX_VISIBLE_FAMILIES = 36;

function radialPosition(rank: number): { x: number; y: number } {
  if (rank === 0) return { x: 500, y: 360 };
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const radius = 54 * Math.sqrt(rank);
  const angle = rank * goldenAngle;
  return {
    x: 500 + Math.cos(angle) * radius,
    y: 360 + Math.sin(angle) * radius,
  };
}

export function buildFamilyElements(args: {
  candidates: ConverseCandidate[];
}): { elements: FamilyElement[]; layout: { name: string; [k: string]: unknown } } {
  const agg = new Map<string, { count: number; mass: number; topP: number }>();
  for (const c of args.candidates) {
    const family = c.family ?? 'Unknown';
    const p = c.probability;
    const cur = agg.get(family) ?? { count: 0, mass: 0, topP: 0 };
    if (p > 0.001) cur.count++;
    cur.mass += p;
    cur.topP = Math.max(cur.topP, p);
    agg.set(family, cur);
  }
  // Rank by probability mass; count is only a readable "active candidate" hint.
  const ordered = Array.from(agg.entries()).sort((a, b) => {
    const dm = b[1].mass - a[1].mass;
    return dm !== 0 ? dm : b[1].topP - a[1].topP;
  });
  const visible = ordered.slice(0, MAX_VISIBLE_FAMILIES - 1);
  const hidden = ordered.slice(MAX_VISIBLE_FAMILIES - 1);
  if (hidden.length > 0) {
    visible.push([
      'Other families',
      hidden.reduce(
        (acc, [_family, v]) => ({
          count: acc.count + v.count,
          mass: acc.mass + v.mass,
          topP: Math.max(acc.topP, v.topP),
        }),
        { count: 0, mass: 0, topP: 0 },
      ),
    ]);
  }
  const labelLimit = visible.length <= 12 ? visible.length : 8;
  const elements: FamilyElement[] = visible.map(([family, v], i) => {
    const showLabel = i < labelLimit;
    return {
      data: {
        id: family,
        label: family,
        displayLabel: showLabel ? family : '',
        count: v.count,
        mass: v.mass,
        sizeScore: Math.sqrt(Math.max(v.mass, 0)) * 100,
        topP: v.topP,
        rank: i,
      },
      position: radialPosition(i),
    };
  });
  return {
    elements,
    layout: {
      name: 'preset',
      fit: true,
      padding: 60,
      animate: true,
      animationDuration: 400,
    },
  };
}

export const familyClusterStylesheet = [
  {
    selector: 'node',
    style: {
      'width': 'mapData(sizeScore, 0, 100, 30, 104)',
      'height': 'mapData(sizeScore, 0, 100, 30, 104)',
      'background-color': colors.sage[500],
      'opacity': 'mapData(sizeScore, 0, 100, 0.5, 1)',
      'label': 'data(displayLabel)',
      'color': colors.ink[900],
      'font-family': 'Source Sans 3, system-ui, sans-serif',
      'font-size': 12,
      'text-valign': 'bottom',
      'text-margin-y': 4,
    },
  },
];
